var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import styled, { withTheme } from 'styled-components';
import { rem, math } from '../utils/Polished';
import PopupTitle from '../PopupTitle';
import PopupContent from '../PopupContent';
import PopupActions from '../PopupActions';
import Typography from '../Typography';
import ButtonOutlined from '../Button/ButtonOutlined';
import Portal from '../utils/Portal';
import Grid, { GridContainerGutter, GridItemGutter, GridTotalColumns } from '../Grid';
import { withForwardedRef } from '../utils/helpers/reactHelpers';
const DialogWidthFor = (breakPoint, columns) => {
    const gridContainerGutterx2 = math(`2 * ${GridContainerGutter[breakPoint]}`);
    const gridItemGutterx2 = math(`2 * ${GridItemGutter[breakPoint]}`);
    return `calc( ( ( 100% - ${gridContainerGutterx2}) * ${columns} / ${GridTotalColumns}) - ${gridItemGutterx2} )`;
};
const DialogSC = styled(Grid) `
  display: flex;
  position: fixed;
  bottom: 0;
  right: 0;
  left: auto;
  top: auto;
  padding: 0;

  ._ndsDialog__content {
    display: flex;
    border-radius: ${rem('4px')};
    box-shadow: ${(props) => props.theme.SizeShadowDialog}
      ${(props) => props.theme.ColorShadowDialogBase};
    background-color: ${(props) => props.theme.ColorBackgroundDialogBase};
    max-width: 100%;

    padding: ${(props) => props.theme.SizePaddingDialogBase};
    @media (min-width: ${(props) => props.theme.SizeBreakpointsM}) {
      padding: ${rem('24px')};
    }
  }

  ._ndsDialog__col1 {
    flex: 0 1 auto;
  }

  ._ndsDialog__col2 {
    flex: 1 1 auto;
  }

  ._ndsDialog_icon {
    margin-right: ${rem('10px')};
  }

  @media (min-width: ${(props) => props.theme.SizeBreakpointsS}) {
    bottom: ${rem('16px')};
    right: ${rem('16px')};
  }

  /* width */
  &.nds-xs-12 {
    width: ${DialogWidthFor('xs', 12)};
    left: 50%;
    transform: translate(-50%);
  }

  @media (min-width: ${(props) => props.theme.SizeBreakpointsS}) {
    &.nds-s-6 {
      width: ${DialogWidthFor('s', 6)};
      left: auto;
      transform: initial;
    }
  }

  @media (min-width: ${(props) => props.theme.SizeBreakpointsM}) {
    &.nds-m-5 {
      width: ${DialogWidthFor('m', 5)};
    }
  }

  @media (min-width: ${(props) => props.theme.SizeBreakpointsL}) {
    &.nds-l-4 {
      width: ${DialogWidthFor('l', 4)};
    }
  }

  @media (min-width: ${(props) => props.theme.SizeBreakpointsXl}) {
    &.nds-xl-3 {
      width: ${DialogWidthFor('xl', 3)};
    }
  }
`;
const Dialog = (props) => {
    const { title, content, actions, onActions, className: classNameProp, icon, theme, id, onClose, open, forwardedRef } = props, other = __rest(props, ["title", "content", "actions", "onActions", "className", "icon", "theme", "id", "onClose", "open", "forwardedRef"]);
    const firstAction = actions[0];
    const secondAction = actions[1];
    const onFirstAction = onActions[0];
    const onSecondAction = onActions[1];
    const handleClose = React.useCallback((event) => {
        if (onClose) {
            onClose(event);
        }
    }, [onClose]);
    let iconComp = null;
    if (icon && React.isValidElement(icon)) {
        const iconProps = icon.props;
        iconComp = React.cloneElement(icon, {
            className: clsx('_ndsDialog_icon', iconProps && iconProps.className),
            color: (iconProps && iconProps.color) || theme.ColorFontPrimary,
            size: 'l'
        });
    }
    return (open && (React.createElement(Portal, null,
        React.createElement(DialogSC, Object.assign({ className: clsx('_ndsDialog', classNameProp), "aria-describedby": id, id: id, ref: forwardedRef, xs: 12, s: 6, m: 5, l: 4, xl: 3 }, other),
            React.createElement("div", { className: "_ndsDialog__content" },
                React.createElement("div", { className: "_ndsDialog__col1" }, iconComp),
                React.createElement("div", { className: "_ndsDialog__col2" },
                    React.createElement(PopupTitle, { onClose: handleClose }, title),
                    React.createElement(PopupContent, null,
                        React.createElement(Typography, { variant: "body2" }, content)),
                    React.createElement(PopupActions, null,
                        firstAction && (React.createElement(ButtonOutlined, { onClick: onFirstAction, color: "grey" }, firstAction)),
                        secondAction && (React.createElement(ButtonOutlined, { onClick: onSecondAction }, secondAction)))))))));
};
Dialog.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    actions(props, propName, componentName) {
        const { [propName]: propValue } = props;
        if (!Array.isArray(propValue)) {
            return new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`. \`actions\` property must be an array.`);
        }
        if (typeof propValue[1] !== 'string' || propValue[1] === '') {
            return new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`. Second value(Primary action) of \`actions\` property must be a non empty string.`);
        }
        return null;
    },
    onActions(props, propName, componentName) {
        const { [propName]: propValue } = props;
        if (!Array.isArray(propValue)) {
            return new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`. \`onActions\` property must be an array.`);
        }
        if (typeof propValue[1] !== 'function') {
            return new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`. Second value(Primary onAction) of \`onActions\` property must be a function.`);
        }
        return null;
    },
    className: PropTypes.string,
    icon: PropTypes.element,
    id: PropTypes.string,
    onClose: PropTypes.func,
    open: PropTypes.bool.isRequired,
    forwardedRef: PropTypes.any
};
Dialog.defaultProps = {
    actions: [],
    onActions: [],
    className: null,
    icon: undefined,
    id: null,
    onClose: null,
    forwardedRef: null
};
const DialogWithTheme = withTheme(Dialog);
DialogWithTheme.displayName = 'Dialog';
export { Dialog as DialogRootComp };
export default withForwardedRef(DialogWithTheme);
//# sourceMappingURL=Dialog.js.map